import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import {
  CreditCard, Building2, BedDouble, CalendarClock, Receipt, Loader2, CheckCircle2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtMoney = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const MODEL_LABELS = { prpm: 'Per Room / Month', flat: 'Flat Monthly Rate', hybrid: 'Base + Per Room' };

export default function MyBilling({ token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/my-billing`, { headers })
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load billing'))
      .finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;
  if (error) return <p className="p-6 text-sm text-muted-foreground">{error}</p>;
  if (!data) return null;

  const billing = data.billing || {};
  const model = billing.pricing_model || 'prpm';
  const rate = model === 'prpm' ? billing.prpm_rate : model === 'flat' ? billing.flat_rate : billing.hybrid_prpm;
  const monthly = model === 'prpm' ? (data.total_rooms * (billing.prpm_rate || 0))
    : model === 'flat' ? (billing.flat_rate || 0)
    : (billing.hybrid_base || 0) + data.total_rooms * (billing.hybrid_prpm || 0);
  const enabledFeatures = Object.values(data.features || {}).filter(Boolean).length;

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-4xl space-y-6" data-testid="my-billing-page">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Billing & Plan</h2>
          <p className="text-xs text-muted-foreground">Your organization's plan, pricing and invoice history</p>
        </div>

        {/* Plan card */}
        <div className="border rounded-xl p-5 bg-gradient-to-br from-violet-50 to-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-violet-600 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-base font-bold capitalize" style={{ fontFamily: 'Space Grotesk' }}>{data.plan || 'Trial'} Plan</div>
              <div className="text-[11px] text-muted-foreground">{MODEL_LABELS[model] || model}{rate ? ` · ${fmtMoney(rate)}${model !== 'flat' ? '/room' : ''}/mo` : ''}</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }} data-testid="billing-monthly-total">{fmtMoney(monthly)}</div>
              <div className="text-[10px] text-muted-foreground">estimated / month</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
            {enabledFeatures} features enabled on your plan
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: Building2, label: 'Properties', value: data.properties },
            { icon: BedDouble, label: 'Total Rooms', value: (data.total_rooms || 0).toLocaleString() },
            { icon: CalendarClock, label: 'Contract End', value: data.contract_end ? data.contract_end.slice(0, 10) : '—' },
            { icon: Receipt, label: 'Next Invoice', value: data.next_invoice_date ? data.next_invoice_date.slice(0, 10) : '—' },
          ].map(s => (
            <div key={s.label} className="border rounded-xl p-4 bg-card">
              <div className="flex items-center gap-2 mb-1">
                <s.icon className="h-4 w-4 text-violet-600" />
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{s.label}</span>
              </div>
              <div className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Invoices */}
        <div>
          <h3 className="text-sm font-semibold mb-2">Invoice History</h3>
          {(data.invoices || []).length === 0 ? (
            <div className="py-10 text-center border rounded-xl">
              <Receipt className="h-8 w-8 text-muted-foreground/25 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No invoices yet.</p>
            </div>
          ) : (
            <div className="border rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr className="text-left text-muted-foreground">
                    <th className="p-2.5 font-semibold">Date</th>
                    <th className="p-2.5 font-semibold">Invoice #</th>
                    <th className="p-2.5 font-semibold">Status</th>
                    <th className="p-2.5 font-semibold text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.invoices.map((inv, i) => (
                    <tr key={inv.id || i} className="hover:bg-muted/20" data-testid={`invoice-row-${i}`}>
                      <td className="p-2.5">{(inv.date || '').slice(0, 10)}</td>
                      <td className="p-2.5 font-mono">{inv.invoice_number || inv.id?.slice(0, 8) || '—'}</td>
                      <td className="p-2.5">
                        <Badge variant={inv.status === 'paid' ? 'default' : 'outline'} className="text-[9px] capitalize">{inv.status || 'pending'}</Badge>
                      </td>
                      <td className="p-2.5 text-right font-bold tabular-nums">{fmtMoney(inv.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-[10px] text-muted-foreground">Questions about your plan or billing? Contact your LodgiFlow account manager{billing.account_manager ? ` (${billing.account_manager})` : ''}.</p>
      </div>
    </ScrollArea>
  );
}
